/**
 * cursorScope — pure helpers that decide which "scope" the rail's
 * chat input refers to (inspector vs. cursor vs. document) and the
 * small ambient line above the input. Spec 26 C1.
 *
 * # DECISION (2026-05-15): cursor branch is back.
 * # WHY: writers reported the chip stays "document" after a draft
 * #      completes even when their cursor is inside a section, so
 * #      they can't tell what scope the next chat message will use.
 * # HOW: the shell now feeds ``resolveScope`` a cursor section number
 * #      sourced from ``detectCursorSectionLight`` (a single-sync
 * #      bookmark lookup), refreshed via the 1500 ms debounced
 * #      ``subscribeSelectionChange`` and gated on ``!isActiveRun`` so
 * #      it never runs while the writer is drafting. This avoids the
 * #      old ``useDocumentCursor`` freeze — that hook ran a full
 * #      backward paragraph walk on every raw selection event during
 * #      mid-run drafts.
 *
 * SRP: data → string mapping. No React, no hooks; reads only the
 * unnumbered-section registry in ./sectionLabel (set by the shell from
 * the doctype config). Tests pin every grammar branch so the auto-scope
 * line never drifts.
 */

import { isUnnumberedSection, unnumberedSectionTitle } from "./sectionLabel";

// ---------------------------------------------------------------------------
// Scope-line formatter
// ---------------------------------------------------------------------------

/**
 * Build the small ambient line shown above the rail input. Returns
 * null when there's nothing meaningful to surface (no inspector
 * section) — caller hides the line entirely in that case so the
 * pane doesn't carry empty chrome.
 */
export function formatScopeLine(inspectorSectionNumber: string | null): string | null {
  if (!inspectorSectionNumber || inspectorSectionNumber.length === 0) {
    return null;
  }
  return `inspecting §${inspectorSectionNumber}`;
}

// ---------------------------------------------------------------------------
// Scope chip resolution — Spec 26 C2
// ---------------------------------------------------------------------------

/** Discriminated scope record. ``kind`` drives chip styling +
 *  downstream chat routing; ``label`` is what the chip shows. */
export type ResolvedScope =
  | { kind: "inspector"; sectionNumber: string; label: string; hint: string }
  | { kind: "cursor"; sectionNumber: string; label: string; hint: string }
  | { kind: "document"; label: string; hint: string };

/** Longest heading title the chip shows before truncating (hint has it all). */
const CHIP_TITLE_MAX = 24;

/** Chip text for a section: "§9.2"; "Title page" for the cover (the section
 *  lookups report it as ``TITLE``); and the heading title for a template's
 *  unnumbered section, whose number ("1-2") is an internal id the document
 *  never shows — the spine hides it too. */
function sectionLabel(sectionNumber: string, full = false): string {
  if (sectionNumber === "TITLE") return "Title page";
  const title = isUnnumberedSection(sectionNumber) ? unnumberedSectionTitle(sectionNumber) : null;
  if (!title) return `§${sectionNumber}`;
  return full || title.length <= CHIP_TITLE_MAX ? title : `${title.slice(0, CHIP_TITLE_MAX - 1).trimEnd()}…`;
}

/**
 * Pick the active scope for the rail's chat input.
 *
 *  - inspector section set → "inspector" (wins — explicit writer focus)
 *  - cursor section set    → "cursor"
 *  - neither                → "document" (the whole doc)
 *
 * ``cursorSectionNumber`` is sourced from ``detectCursorSectionLight`` by the
 * shell's debounced + run-gated selection-change subscriber. ``handleSendChat``
 * still does its own on-demand cursor read at send time as the ground-truth
 * fallback when the chip-level state is stale.
 */
export function resolveScope(
  inspectorSectionNumber: string | null,
  cursorSectionNumber: string | null = null,
): ResolvedScope {
  if (inspectorSectionNumber && inspectorSectionNumber.length > 0) {
    return {
      kind: "inspector",
      sectionNumber: inspectorSectionNumber,
      label: sectionLabel(inspectorSectionNumber),
      hint: `Inspector open on ${sectionLabel(inspectorSectionNumber, true)}.`,
    };
  }
  if (cursorSectionNumber && cursorSectionNumber.length > 0) {
    return {
      kind: "cursor",
      sectionNumber: cursorSectionNumber,
      label: sectionLabel(cursorSectionNumber),
      hint: `Cursor is in ${sectionLabel(cursorSectionNumber, true)}.`,
    };
  }
  return {
    kind: "document",
    label: "document",
    hint: "Nothing focused — questions apply to the whole document.",
  };
}
